/**
 * MatchDetails - Detail panel for selected match
 *
 * Features:
 * - Match info display
 * - Photographer/Editor assignments
 * - IPTC template editor (Photo Mechanic style fields)
 * - FTP server assignment
 * - Editor sign-off for live events
 */

import { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import Button from '../../../../shared/components/ui/Button';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';
import { formatDateTime, getMatchStatus, getRelativeTime } from '../../utils/dateUtils';
import {
  getEventColor,
  getCompetitionLabel,
  getEventTypeLabel,
} from '../../utils/constants';
import useCompetitions from '../../hooks/useCompetitions';
import { getMatchXmpUrl } from '../../../../api';
import {
  Btn,
  Kbd,
  Stat,
  StatusPill,
  TabBar,
} from '../../../../shared/components/redesign';
import LineupTab from '../LineupTab';
import styles from './MatchDetails.module.css';

/**
 * IPTC fields matching API MatchIptcConfig model
 */
const IPTC_FIELDS = [
  { key: 'headline_template', label: 'Headline', type: 'text' },
  { key: 'caption_template', label: 'Caption/Description', type: 'textarea' },
  { key: 'keywords', label: 'Keywords', type: 'text', hint: 'Comma separated' },
  { key: 'title_template', label: 'Title', type: 'text' },
  { key: 'event_name', label: 'Event Name', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State/Province', type: 'text' },
  { key: 'country', label: 'Country', type: 'text' },
  { key: 'country_code', label: 'Country Code', type: 'text' },
  { key: 'location', label: 'Location', type: 'text' },
  { key: 'credit', label: 'Credit', type: 'text' },
  { key: 'copyright_notice', label: 'Copyright Notice', type: 'text' },
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'supplemental_categories', label: 'Suppl. Categories', type: 'text', hint: 'Comma separated' },
];

/**
 * Section header component
 */
function SectionHeader({ title, action }) {
  return (
    <div className={styles.sectionHeader}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {action}
    </div>
  );
}

/**
 * Single labeled mono card used in the 2×2 schedule grid.
 * Source: design_handoff_tank/screens-matches.jsx § Schedule field.
 */
function ScheduleField({ label, value }) {
  return (
    <div className={styles.scheduleField}>
      <span className={styles.scheduleLabel}>{label}</span>
      <span className={styles.scheduleValue}>{value}</span>
    </div>
  );
}

/**
 * Initials avatar for a crew row. Reads from any of:
 *   - {first_name, last_name}
 *   - {username}  (e.g. "yannick.verhoeven" → "YV")
 *   - {full_name} or {name}  (single string → first letter of each word)
 *
 * Crew assignments arrive as `{user: {...}}` from MatchSerializer, but
 * many call sites pass a flat assignment with `username`/`user_name`
 * at the top level. Accept either by treating both shapes the same.
 */
function Avatar({ user }) {
  const u = user || {};
  let initials = '';
  if (u.first_name || u.last_name) {
    initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase();
  }
  if (!initials && (u.full_name || u.name)) {
    const parts = String(u.full_name || u.name).trim().split(/\s+/);
    initials = parts
      .slice(0, 2)
      .map((p) => p[0] || '')
      .join('')
      .toUpperCase();
  }
  if (!initials && u.username) {
    // Username may be dotted (`yannick.verhoeven`) or single-word.
    const parts = String(u.username).split(/[._-]+/).filter(Boolean);
    initials =
      parts.length >= 2
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : (u.username.slice(0, 2) || '').toUpperCase();
  }
  return <span className={styles.avatar}>{initials || '·'}</span>;
}

/**
 * Crew row list — single role at a time. Used twice inside the unified
 * Crew card, once for Photographer and once for Editor (label per row).
 */
function CrewList({
  assignments,
  role,
  showSignOff = false,
  onSignOff,
  onUnassign,
  canManage,
  requestConfirm,
}) {
  if (!assignments || assignments.length === 0) return null;
  return (
    <ul className={styles.crewListNew}>
      {assignments.map((a) => {
        const user = a.user || {};
        const fullName =
          `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
          a.username ||
          a.user_name ||
          `User ${a.user_id}`;
        // The Avatar reads first/last/username/full_name; a flat
        // assignment may carry only `username` at top level, so merge it
        // in so initials work regardless of which shape we got.
        const avatarUser = {
          ...user,
          username: user.username || a.username || a.user_name,
          full_name: user.full_name || a.user_name || fullName,
        };
        return (
          <li key={a.id || user.id} className={styles.crewRowNew}>
            <Avatar user={avatarUser} />
            <span className={styles.crewName}>{fullName}</span>
            <span className={styles.crewRole}>{role}</span>
            {showSignOff && a.signed_off && (
              <span className={styles.signedOff}>Signed off</span>
            )}
            {showSignOff && !a.signed_off && onSignOff && (
              <button
                type="button"
                className={styles.crewSignOff}
                onClick={() => onSignOff(a)}
              >
                Sign off
              </button>
            )}
            {canManage && (
              <button
                type="button"
                className={styles.crewRemove}
                aria-label={`Remove ${fullName}`}
                onClick={() =>
                  requestConfirm({
                    title: `Remove ${role.toLowerCase()}?`,
                    message: `Remove ${fullName} from this match?`,
                    confirmLabel: 'Remove',
                    onConfirm: () => onUnassign(a.user_id || a.user),
                  })
                }
              >
                ×
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Inline assign picker — opens a small dropdown of available users.
 */
function CrewAddPicker({ label, available, onPick }) {
  const [open, setOpen] = useState(false);
  if (!available || available.length === 0) return null;
  return (
    <div className={styles.crewAddPicker}>
      <button
        type="button"
        className={styles.crewAddBtn}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>
      {open && (
        <ul className={styles.crewAddMenu}>
          {available.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className={styles.crewAddItem}
                onClick={() => {
                  onPick(u.id);
                  setOpen(false);
                }}
              >
                {u.first_name || ''} {u.last_name || ''} {u.username && `@${u.username}`}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Assignment list component
 */
function AssignmentList({
  title,
  assignments,
  availableUsers,
  onAssign,
  onUnassign,
  canManage,
  showSignOff = false,
  onSignOff,
  requestConfirm,
}) {
  const [showAdd, setShowAdd] = useState(false);

  const unassignedUsers = useMemo(() => {
    const assignedIds = new Set(assignments.map((a) => a.user_id || a.user));
    return availableUsers.filter((u) => !assignedIds.has(u.id));
  }, [assignments, availableUsers]);

  return (
    <div className={styles.assignmentSection}>
      <SectionHeader
        title={title}
        action={
          canManage && unassignedUsers.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdd(!showAdd)}
            >
              {showAdd ? 'Cancel' : '+ Add'}
            </Button>
          )
        }
      />

      {assignments.length === 0 ? (
        <p className={styles.emptyNote}>No one assigned yet</p>
      ) : (
        <ul className={styles.assignmentList}>
          {assignments.map((assignment) => (
            <li key={assignment.id} className={styles.assignmentItem}>
              <span className={styles.assignmentName}>
                {assignment.username || assignment.user_name || `User ${assignment.user_id}`}
              </span>
              <div className={styles.assignmentActions}>
                {showSignOff && assignment.signed_off && (
                  <span className={styles.signedOff}>Signed off</span>
                )}
                {showSignOff && !assignment.signed_off && onSignOff && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSignOff(assignment)}
                  >
                    Sign off
                  </Button>
                )}
                {canManage && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      const name =
                        assignment.username ||
                        assignment.user_name ||
                        `User ${assignment.user_id}`;
                      requestConfirm({
                        title: `Remove ${title.toLowerCase().replace(/s$/, '')}?`,
                        message: `Remove ${name} from this match?`,
                        confirmLabel: 'Remove',
                        onConfirm: () => onUnassign(assignment.user_id || assignment.user),
                      });
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <div className={styles.addAssignment}>
          {unassignedUsers.map((user) => (
            <Button
              key={user.id}
              variant="ghost"
              size="sm"
              onClick={() => {
                onAssign(user.id);
                setShowAdd(false);
              }}
            >
              + {user.username || user.name}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * IPTC Template Editor
 */
function IPTCEditor({ template, onChange, onSave, onDownload, canEdit }) {
  const [localTemplate, setLocalTemplate] = useState(template || {});
  const [isDirty, setIsDirty] = useState(false);

  const handleChange = (key, value) => {
    setLocalTemplate((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    onChange(localTemplate);
    onSave?.(localTemplate);
    setIsDirty(false);
  };

  // Array fields that need join/split handling
  const ARRAY_FIELDS = ['keywords', 'supplemental_categories'];

  // Get display value for a field - handle array fields specially
  const getFieldValue = (key) => {
    const value = localTemplate[key];
    if (ARRAY_FIELDS.includes(key) && Array.isArray(value)) {
      return value.join(', ');
    }
    return value || '';
  };

  // Set value for a field - convert array fields back to arrays
  const setFieldValue = (key, value) => {
    if (ARRAY_FIELDS.includes(key)) {
      const items = value
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k);
      handleChange(key, items);
    } else {
      handleChange(key, value);
    }
  };

  return (
    <div className={styles.iptcEditor}>
      <SectionHeader
        title="IPTC Template"
        action={
          <div className={styles.iptcActions}>
            {onDownload && (
              <Button variant="ghost" size="sm" onClick={() => onDownload(localTemplate)}>
                Download
              </Button>
            )}
            {canEdit && isDirty && (
              <Button variant="primary" size="sm" onClick={handleSave}>
                Save Template
              </Button>
            )}
          </div>
        }
      />

      <div className={styles.iptcFields}>
        {IPTC_FIELDS.map((field) => (
          <div key={field.key} className={styles.iptcField}>
            <label className={styles.iptcLabel}>
              {field.label}
              {field.hint && <span className={styles.iptcHint}>{field.hint}</span>}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                className={styles.iptcTextarea}
                value={getFieldValue(field.key)}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
                disabled={!canEdit}
                rows={3}
              />
            ) : (
              <input
                type="text"
                className={styles.iptcInput}
                value={getFieldValue(field.key)}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
                disabled={!canEdit}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * FTP Server Assignment
 */
function FTPServers({ servers, assignedServers, onAssign, onUnassign, canManage, requestConfirm }) {
  const unassigned = servers.filter((s) => !assignedServers.includes(s.id));
  const assigned = servers.filter((s) => assignedServers.includes(s.id));

  return (
    <div className={styles.ftpSection}>
      <SectionHeader title="FTP Servers" />

      {assigned.length === 0 ? (
        <p className={styles.emptyNote}>No FTP servers assigned</p>
      ) : (
        <ul className={styles.ftpList}>
          {assigned.map((server) => (
            <li key={server.id} className={styles.ftpItem}>
              <span className={styles.ftpName}>{server.name}</span>
              <span className={styles.ftpHost}>{server.host}</span>
              {canManage && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    requestConfirm({
                      title: 'Remove FTP target?',
                      message: `Remove FTP target "${server.name}" from this match?`,
                      confirmLabel: 'Remove',
                      onConfirm: () => onUnassign(server.id),
                    });
                  }}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && unassigned.length > 0 && (
        <div className={styles.addFtp}>
          <select
            className={styles.ftpSelect}
            onChange={(e) => {
              if (e.target.value) {
                onAssign(e.target.value);
                e.target.value = '';
              }
            }}
            defaultValue=""
          >
            <option value="">Add FTP server...</option>
            {unassigned.map((server) => (
              <option key={server.id} value={server.id}>
                {server.name} ({server.host})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Main MatchDetails component
 */
export default function MatchDetails({
  match,
  // False while `match` is still the list row (no FTP links, competition or
  // IPTC config): Edit and the FTP tab wait, so nothing saves blanks over
  // the real match.
  detailsLoaded = false,
  photographers = [],
  editors = [],
  ftpServers = [],
  onEdit,
  onDelete,
  onClose,
  onAssignPhotographer,
  onUnassignPhotographer,
  onAssignEditor,
  onUnassignEditor,
  onEditorSignOff,
  onSaveIPTC,
  onDownloadIPTC,
  onAssignFTP,
  onUnassignFTP,
  onMarkFullTime,
  onUndoFullTime,
  onLineupChanged,
  canManage = false,
  canConfirmLineup = false,
  canSetFullTime = false,
  canManagePersons = false,
  isLive = false,
}) {
  const { bySlug } = useCompetitions();
  const [activeTab, setActiveTab] = useState('info');
  const [confirmState, setConfirmState] = useState(null);
  const status = match ? getMatchStatus(match) : null;
  const eventColor = match ? getEventColor(bySlug, match) : null;

  // Clear any pending confirm when the selected match changes or the drawer closes
  useEffect(() => {
    setConfirmState(null);
  }, [match?.id]);

  const requestConfirm = (options) => setConfirmState(options);
  const closeConfirm = () => setConfirmState(null);
  const handleConfirm = () => {
    try {
      confirmState?.onConfirm?.();
    } finally {
      closeConfirm();
    }
  };

  if (!match) {
    return (
      <div className={styles.empty}>
        <p>Select a match to view details</p>
      </div>
    );
  }

  const photographerAssignments = match.photographer_assignments || [];
  const editorAssignments = match.editor_assignments || [];
  // API returns ftp_target_assignments with nested ftp_target_id
  const assignedFTPServers = (match.ftp_target_assignments || []).map(a => a.ftp_target_id);

  // Show sign-off option when live with 2+ editors
  const showEditorSignOff = isLive && editorAssignments.length >= 2;

  return (
    <div className={styles.container}>
      {/* Header — eyebrow includes competition + round; subtitle stacks
          date · kick-off · venue per design spec. */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          {/* Phos left-bar — always phosphor (no per-event-type color)
              per design spec; the !important in CSS makes the inline style
              irrelevant but we drop it here for clarity. */}
          <div className={styles.colorIndicator} aria-hidden="true" />
          <div className={styles.headerText}>
            <span className={styles.eyebrow}>
              {[
                '§ MATCH',
                match.competition && getCompetitionLabel(bySlug, match.competition),
                match.round != null && match.round !== '' && `Round ${match.round}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <h2 className={styles.title}>
              {match.home_team_name && match.away_team_name
                ? `${match.home_team_name} vs ${match.away_team_name}`
                : match.name || 'Untitled Event'}
            </h2>
            <p className={styles.subtitle}>
              {[
                match.start_time
                  ? new Date(match.start_time).toLocaleDateString(undefined, {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })
                  : null,
                match.kick_off_time
                  ? `Kick-off ${new Date(match.kick_off_time).toLocaleTimeString(
                      [],
                      { hour: '2-digit', minute: '2-digit' }
                    )}`
                  : null,
                [match.venue, match.city].filter(Boolean).join(', ') || null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {/* Match.subject_pronoun, visible without opening the wizard: this
                is the word every goal phrase renders for this fixture. */}
            {(match.event_type || 'match') === 'match' && (
              <span
                className={styles.pronoun}
                title="Caption pronoun for this fixture (his / her / their). Change it under Edit."
              >
                Captions: &quot;{match.subject_pronoun || 'his'} team&apos;s first goal&quot;
              </span>
            )}
            {status === 'upcoming' && (
              <span className={styles.relative}>
                {getRelativeTime(match.start_time)}
              </span>
            )}
          </div>
        </div>
        <div className={styles.headerActions}>
          {onClose && (
            <button
              type="button"
              className={styles.closeButton}
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Tabs — shared TabBar primitive. FTP shows a count for the
          number of assigned destinations. */}
      <div className={styles.tabsWrapper}>
        <TabBar
          active={activeTab}
          onChange={setActiveTab}
          tabs={[
            { id: 'info', label: 'Info' },
            { id: 'iptc', label: 'IPTC' },
            {
              id: 'ftp',
              label: 'FTP',
              count: assignedFTPServers.length > 0 ? assignedFTPServers.length : undefined,
            },
            { id: 'crew', label: 'Crew' },
            {
              id: 'lineup',
              label: 'XI',
              // Serializer summary — non-null means a confirmed scan (the
              // green XI state); the count mirrors the chip's matched total.
              count: match.lineup_scan ? match.lineup_scan.matched_count : undefined,
            },
          ]}
        />
      </div>

      {/* Tab content */}
      <div className={styles.tabContent}>
        {activeTab === 'info' && (
          <>
            {/* Schedule — 2x2 grid of mono cards (Start / End / Kick-off /
                Round). Source: design_handoff_tank/screens-matches.jsx
                § Screen03MatchesDrawerA Schedule. */}
            <div className={styles.infoSection}>
              <span className={styles.sectionEyebrow}>Schedule</span>
              <div className={styles.scheduleGrid}>
                <ScheduleField
                  label="Start"
                  value={
                    match.start_time
                      ? formatDateTime(match.start_time, { year: undefined })
                      : '—'
                  }
                />
                <ScheduleField
                  label="End"
                  value={
                    match.end_time
                      ? formatDateTime(match.end_time, { year: undefined })
                      : '—'
                  }
                />
                <ScheduleField
                  label="Kick-off"
                  value={
                    match.kick_off_time
                      ? formatDateTime(match.kick_off_time, { year: undefined })
                      : '—'
                  }
                />
                <ScheduleField
                  label={match.full_time_at ? 'Full time' : 'Round'}
                  value={
                    match.full_time_at
                      ? formatDateTime(match.full_time_at, { year: undefined })
                      : match.round != null && match.round !== ''
                        ? String(match.round)
                        : '—'
                  }
                />
              </div>
              <div className={styles.scheduleActions}>
                {canSetFullTime && match.kick_off_time && !match.full_time_at && isLive && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      if (
                        confirm(
                          'Mark official playing time as ended? This affects photo captions.'
                        )
                      ) {
                        onMarkFullTime();
                      }
                    }}
                  >
                    Full Time
                  </Button>
                )}
                {canSetFullTime && match.full_time_at && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (confirm('Undo full time marking?')) {
                        onUndoFullTime();
                      }
                    }}
                  >
                    Undo Full Time
                  </Button>
                )}
              </div>
            </div>

            {/* Crew — unified list with role badges + remove × + assign rows.
                Source: screens-matches.jsx § Screen03MatchesDrawerA Crew. */}
            <div className={styles.infoSection}>
              <span className={styles.sectionEyebrow}>Crew</span>
              <div className={styles.crewCard}>
                <CrewList
                  assignments={photographerAssignments}
                  role="Photographer"
                  showSignOff={false}
                  onUnassign={onUnassignPhotographer}
                  onSignOff={null}
                  canManage={canManage}
                  requestConfirm={requestConfirm}
                />
                <CrewList
                  assignments={editorAssignments}
                  role="Editor"
                  showSignOff={showEditorSignOff}
                  onUnassign={onUnassignEditor}
                  onSignOff={onEditorSignOff}
                  canManage={canManage}
                  requestConfirm={requestConfirm}
                />
                {canManage && (
                  <div className={styles.crewAdd}>
                    <CrewAddPicker
                      label="+ Assign editor"
                      available={editors.filter(
                        (u) =>
                          !editorAssignments.some(
                            (a) => (a.user_id || a.user) === u.id
                          )
                      )}
                      onPick={onAssignEditor}
                    />
                    <CrewAddPicker
                      label="+ Assign photographer"
                      available={photographers.filter(
                        (u) =>
                          !photographerAssignments.some(
                            (a) => (a.user_id || a.user) === u.id
                          )
                      )}
                      onPick={onAssignPhotographer}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* FTP destinations — read view of assigned servers with status
                pill. Management (add/remove) lives in the FTP tab.
                Source: design_handoff_tank/screens-matches.jsx § Drawer Info. */}
            {ftpServers.length > 0 && assignedFTPServers.length > 0 && (
              <div className={styles.infoSection}>
                <span className={styles.sectionEyebrow}>
                  FTP destinations · {assignedFTPServers.length}
                </span>
                <div className={styles.ftpDestList}>
                  {ftpServers
                    .filter((s) => assignedFTPServers.includes(s.id))
                    .map((server) => {
                      const a = (match.ftp_target_assignments || []).find(
                        (x) => x.ftp_target_id === server.id
                      );
                      const exportStatus = a?.export_status || 'pending';
                      const pillKind =
                        exportStatus === 'exported'
                          ? 'exported'
                          : exportStatus === 'error'
                            ? 'error'
                            : 'pending';
                      const pillLabel =
                        exportStatus === 'exported'
                          ? 'Exported'
                          : exportStatus === 'error'
                            ? 'Error'
                            : 'Pending';
                      return (
                        <div key={server.id} className={styles.ftpDestRow}>
                          <span className={styles.ftpDestIcon}>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect x="2" y="4" width="20" height="6" rx="1" />
                              <rect x="2" y="14" width="20" height="6" rx="1" />
                              <line x1="6" y1="7" x2="6" y2="7" />
                              <line x1="6" y1="17" x2="6" y2="17" />
                            </svg>
                          </span>
                          <span className={styles.ftpDestText}>
                            <span className={styles.ftpDestName}>
                              {server.name}
                            </span>
                            <span className={styles.ftpDestHost}>
                              {server.host}
                            </span>
                          </span>
                          <StatusPill kind={pillKind} compact>
                            {pillLabel}
                          </StatusPill>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Stats — 4 phosphor-tinted tiles (Photos / Done / Exported /
                Errors). Uses shared `Stat` atom. */}
            <div className={styles.infoSection}>
              <span className={styles.sectionEyebrow}>Stats</span>
              <div className={styles.statTiles}>
                <Stat
                  label="Photos"
                  value={match.photo_count || 0}
                  kind="pending"
                />
                <Stat
                  label="Done"
                  value={match.photo_status_summary?.done || 0}
                  kind="done"
                />
                <Stat
                  label="Exported"
                  value={match.photo_status_summary?.exported || 0}
                  kind="exported"
                />
                <Stat
                  label="Errors"
                  value={match.photo_status_summary?.error || 0}
                  kind="error"
                />
              </div>
            </div>
          </>
        )}

        {activeTab === 'iptc' && (
          <>
            {match.iptc_config === undefined ? (
              <p className={styles.emptyNote}>Loading IPTC template…</p>
            ) : (
              <IPTCEditor
                key={match.id}
                template={match.iptc_config || {}}
                onChange={() => {}}
                onSave={onSaveIPTC}
                onDownload={onDownloadIPTC}
                canEdit={canManage}
              />
            )}
            {match.iptc_config && (
              <div className={styles.xmpDownload}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(getMatchXmpUrl(match.id), '_blank')}
                >
                  Download XMP
                </Button>
              </div>
            )}
          </>
        )}

        {activeTab === 'ftp' && !detailsLoaded && (
          <p className={styles.emptyNote}>Loading FTP destinations…</p>
        )}

        {activeTab === 'ftp' && detailsLoaded && (
          <FTPServers
            servers={ftpServers}
            assignedServers={assignedFTPServers}
            onAssign={onAssignFTP}
            onUnassign={onUnassignFTP}
            canManage={canManage}
            requestConfirm={requestConfirm}
          />
        )}

        {activeTab === 'crew' && (
          <div className={styles.infoSection}>
            <span className={styles.sectionEyebrow}>Crew</span>
            <div className={styles.crewCard}>
              {photographerAssignments.length === 0 &&
              editorAssignments.length === 0 ? (
                <p className={styles.emptyNote}>No one assigned yet.</p>
              ) : (
                <>
                  <CrewList
                    assignments={photographerAssignments}
                    role="Photographer"
                    showSignOff={false}
                    onUnassign={onUnassignPhotographer}
                    onSignOff={null}
                    canManage={canManage}
                    requestConfirm={requestConfirm}
                  />
                  <CrewList
                    assignments={editorAssignments}
                    role="Editor"
                    showSignOff={showEditorSignOff}
                    onUnassign={onUnassignEditor}
                    onSignOff={onEditorSignOff}
                    canManage={canManage}
                    requestConfirm={requestConfirm}
                  />
                </>
              )}
              {canManage && (
                <div className={styles.crewAdd}>
                  <CrewAddPicker
                    label="+ Assign editor"
                    available={editors.filter(
                      (u) =>
                        !editorAssignments.some(
                          (a) => (a.user_id || a.user) === u.id
                        )
                    )}
                    onPick={onAssignEditor}
                  />
                  <CrewAddPicker
                    label="+ Assign photographer"
                    available={photographers.filter(
                      (u) =>
                        !photographerAssignments.some(
                          (a) => (a.user_id || a.user) === u.id
                        )
                    )}
                    onPick={onAssignPhotographer}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'lineup' && (
          <LineupTab
            match={match}
            canConfirmLineup={canConfirmLineup}
            canManagePersons={canManagePersons}
            requestConfirm={requestConfirm}
            onChanged={onLineupChanged}
          />
        )}
      </div>

      {/* Persistent footer — Delete (danger, left) · ESC hint · Edit · Close.
          Source: design_handoff_tank/screens-matches.jsx § Drawer footer. */}
      {(canManage || onClose) && (
        <div className={styles.drawerFooter}>
          {canManage && (
            <Btn
              kind="danger"
              size="sm"
              onClick={() =>
                requestConfirm({
                  title: 'Delete match?',
                  message:
                    'This permanently removes the match. Any photos already linked to it stay on disk.',
                  confirmLabel: 'Delete',
                  onConfirm: onDelete,
                })
              }
            >
              Delete match
            </Btn>
          )}
          <span className={styles.escHint}>
            <Kbd>ESC</Kbd> to close
          </span>
          <span className={styles.footerSpacer} />
          {onClose && (
            <Btn kind="ghost" size="sm" onClick={onClose}>
              Close
            </Btn>
          )}
          {canManage && (
            <Btn kind="primary" size="sm" onClick={onEdit} disabled={!detailsLoaded}>
              Edit match
            </Btn>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}
